import { useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useJsApiLoader, GoogleMap, MarkerF } from '@react-google-maps/api';
import { useGetAppSettingsQuery } from '../../store/siteContentApi';

// Live map shown on the customer's Track page. Renders two markers:
//   • the patient's geocoded collection address (static)
//   • the trustian's latest live position (animated as updates arrive)
// Auto-fits the viewport to include both whenever the trustian marker is
// present, otherwise just centers on the patient with a neutral zoom.

const containerStyle = { width: '100%', height: '100%' };

// Off-white, low-contrast base so the colourful markers read clearly.
// We keep POI / labels on so the trustian can roughly orient by landmarks.
const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
};

// Inner — receives a guaranteed non-empty `apiKey` from the wrapper.
// Why split: useJsApiLoader injects the Google Maps <script> tag on
// first render and de-dupes by id. If the very first call is made with
// an empty key (which happens before useGetAppSettingsQuery resolves),
// the script loads in "for development purposes only" mode and stays
// that way for the rest of the session.
const LiveTrackingMapInner = ({ apiKey, patientLocation, trustianLocation, hint }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'trustlab-maps-script',
    googleMapsApiKey: apiKey,
  });

  const mapRef = useRef(null);

  // Fit-bounds once both points are known. We only re-fit on the first
  // time the trustian appears — after that we let the user pan freely and
  // we `panTo` the new trustian position so the view doesn't jump back
  // every 10 seconds.
  const didInitialFit = useRef(false);
  useEffect(() => {
    if (!mapRef.current || !patientLocation) return;
    if (!trustianLocation) {
      mapRef.current.panTo(patientLocation);
      return;
    }
    if (!didInitialFit.current && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(patientLocation);
      bounds.extend(trustianLocation);
      mapRef.current.fitBounds(bounds, 80);
      didInitialFit.current = true;
    } else {
      mapRef.current.panTo(trustianLocation);
    }
  }, [patientLocation, trustianLocation]);

  const defaultCenter = useMemo(
    () => patientLocation || trustianLocation || { lat: 17.385, lng: 78.4867 },
    [patientLocation, trustianLocation],
  );

  if (loadError) {
    return (
      <Box sx={{ ...placeholderSx, bgcolor: '#FEE2E2', color: '#991B1B' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          Couldn't load the map. Check the Google Maps API key.
        </Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={placeholderSx}>
        <Typography sx={{ fontSize: 13, color: 'var(--stone)' }}>Loading map…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={14}
        options={mapOptions}
        onLoad={(m) => { mapRef.current = m; }}
      >
        {patientLocation && (
          <MarkerF
            position={patientLocation}
            title="Collection address"
            label={{ text: '🏠', fontSize: '20px' }}
          />
        )}
        {trustianLocation && (
          <MarkerF
            position={trustianLocation}
            title="Trustian"
            label={{ text: '🏍️', fontSize: '22px' }}
            zIndex={5}
          />
        )}
      </GoogleMap>

      {hint && (
        <Box
          sx={{
            position: 'absolute', top: 12, left: 12, right: 12,
            bgcolor: 'rgba(255,255,255,0.95)', color: 'var(--forest)',
            px: 1.75, py: 0.875, borderRadius: '10px',
            fontSize: 12, fontWeight: 700, textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            pointerEvents: 'none',
          }}
        >
          {hint}
        </Box>
      )}
    </Box>
  );
};

const placeholderSx = {
  width: '100%', height: '100%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'linear-gradient(135deg, #E7F5ED 0%, #C8EDD8 100%)',
};

// Outer wrapper — resolves the Google Maps API key from AppSettings and
// only mounts the inner (which calls useJsApiLoader) once a non-empty
// key is available.
const LiveTrackingMap = (props) => {
  const { data: appSettings, isLoading, isError, error } = useGetAppSettingsQuery();
  // siteContentApi.getAppSettings has transformResponse: raw => raw.settings,
  // so `appSettings` IS the settings object directly (not the wrapper).
  const apiKey = appSettings?.google_maps_api_key
    || process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    || '';

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[LiveTrackingMap] maps key debug:', {
      isLoading,
      isError,
      error,
      hasAppSettings: !!appSettings,
      keyFromSettings: appSettings?.google_maps_api_key ? 'present' : 'missing',
      keyFromEnv: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 'present' : 'missing',
      resolvedKeyLen: apiKey.length,
    });
  }
  if (!apiKey) {
    return (
      <Box sx={placeholderSx}>
        <Typography sx={{ color: 'var(--forest)', fontSize: 13, fontWeight: 600 }}>
          Loading map…
        </Typography>
      </Box>
    );
  }
  return <LiveTrackingMapInner apiKey={apiKey} {...props} />;
};

export default LiveTrackingMap;
